import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Siren, ChevronRight, Camera, X, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { PortalPage, Card, Btn, Spinner, ErrorNote } from "@/components/PortalPage";
import { inputCls } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/lso";
import { fileToBase64 } from "@/pages/compliance/Compliance";

const SEVERITIES = [["no_injury", "No injury"], ["first_aid", "First aid"], ["medical_treatment", "Medical treatment"], ["lost_time", "Lost time"], ["reportable", "Reportable (OSHA)"]];
const PPE = ["Eyewear OD 6+", "FR jacket", "Gloves", "Respirator", "Face shield", "Hearing protection"];

const Photos = ({ photos, setPhotos }) => {
  const ref = useRef(null);
  const add = async (e) => {
    const files = [...(e.target.files || [])].slice(0, 6 - photos.length);
    e.target.value = "";
    const next = await Promise.all(files.map(async (f) => ({ filename: f.name, data_b64: await fileToBase64(f), preview: URL.createObjectURL(f) })));
    setPhotos([...photos, ...next]);
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={add} data-testid="safety-photo-input" />
      <div className="flex flex-wrap gap-2">
        {photos.map((p, i) => (
          <div key={i} className="relative"><img src={p.preview} alt="" className="h-20 w-20 rounded-lg object-cover" />
            <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label="Remove photo" className="absolute -right-2 -top-2 rounded-full bg-black p-1"><X size={12} /></button></div>
        ))}
        {photos.length < 6 && <button onClick={() => ref.current?.click()} data-testid="safety-add-photo" className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#444] text-[10px] text-neutral-400"><Camera size={18} />Add photo</button>}
      </div>
    </div>
  );
};
const strip = (photos) => photos.map(({ filename, data_b64 }) => ({ filename, data_b64 }));

export function SafetyCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const canSubmit = user.role === "franchisee";
  useEffect(() => {
    Promise.all([api("/safety/incidents/me").catch(() => []), api("/safety/checklists/me").catch(() => [])]).then(([i, c]) => setData({ incidents: Array.isArray(i) ? i : i?.incidents || [], checklists: Array.isArray(c) ? c : c?.checklists || [] }));
  }, []);
  const Action = ({ icon: Icon, title, body, to, testId, color }) => (
    <button disabled={!canSubmit} onClick={() => navigate(to)} data-testid={testId} className="flex w-full items-center gap-4 rounded-2xl border border-[#333] bg-[#1A1A1A] p-5 text-left hover:border-[var(--accent)] disabled:opacity-50">
      <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `${color}22`, color }}><Icon size={24} /></span>
      <span className="flex-1"><span className="block font-bold">{title}</span><span className="text-xs text-neutral-400">{body}</span></span><ChevronRight size={18} className="text-neutral-500" />
    </button>
  );
  return (
    <PortalPage eyebrow="ANSI Z136.1 · OPS MANUAL" title="Safety Center" testId="safety-page">
      <div className="grid gap-3 sm:grid-cols-2">
        <Action icon={ClipboardCheck} title="Pre-Job Checklist" body="6 ANSI-aligned sections · sign before every job" to="/safety/jobsite-checklist" testId="safety-new-checklist" color="#4CCC06" />
        <Action icon={Siren} title="Report an Incident" body="Eye/skin exposure, fire, equipment or PPE failure, near miss" to="/safety/incident-report" testId="safety-new-incident" color="#FF3333" />
      </div>
      {!canSubmit && <p className="text-xs text-neutral-500">Checklists and incident reports are submitted by the franchise owner.</p>}
      {!data ? <Spinner /> : (
        <div className="grid gap-6 md:grid-cols-2">
          <section className="space-y-2" data-testid="safety-incident-list">
            <h2 className="text-[11px] font-bold tracking-[0.3em] text-neutral-400">MY INCIDENT REPORTS</h2>
            {data.incidents.length === 0 && <p className="text-sm text-neutral-500">None filed.</p>}
            {data.incidents.map((i) => (
              <Card key={i.id} className="p-4"><div className="flex justify-between gap-2"><p className="font-semibold">{i.incident_type_title}</p><span className={`text-[10px] font-extrabold ${i.status === "closed" ? "text-[#22C55E]" : "text-[#F97316]"}`}>{i.status.toUpperCase()}</span></div>
                <p className="text-xs text-neutral-400">{(SEVERITIES.find(([k]) => k === i.severity) || [0, i.severity])[1]} · {i.location} · {fmtDate(i.created_at)}</p></Card>
            ))}
          </section>
          <section className="space-y-2" data-testid="safety-checklist-list">
            <h2 className="text-[11px] font-bold tracking-[0.3em] text-neutral-400">MY CHECKLISTS</h2>
            {data.checklists.length === 0 && <p className="text-sm text-neutral-500">None submitted.</p>}
            {data.checklists.map((c) => (
              <Card key={c.id} className="p-4"><div className="flex justify-between gap-2"><p className="font-semibold">{c.jobsite}</p><span className={`text-[10px] font-extrabold ${c.any_failures ? "text-[#FF3333]" : "text-[var(--accent)]"}`}>{c.any_failures ? `${c.summary.fail} FAIL` : "ALL CLEAR"}</span></div>
                <p className="text-xs text-neutral-400">{fmtDate(c.job_date)} · {c.summary.pass} pass · {c.summary.na} n/a</p></Card>
            ))}
          </section>
        </div>
      )}
    </PortalPage>
  );
}

export function JobsiteChecklist() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [template, setTemplate] = useState(null);
  const [states, setStates] = useState({});
  const [f, setF] = useState({ jobsite: "", customer_name: "", job_date: new Date().toISOString().slice(0, 10), operator_signature: user.full_name || "", free_notes: "" });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { api("/safety/ansi-refs").then((r) => setTemplate(r.checklist_template)).catch((e) => setErr(e.message)); }, []);
  const total = template?.reduce((n, s) => n + s.items.length, 0) || 0;
  const done = Object.keys(states).length;

  const submit = async () => {
    if (!f.jobsite.trim()) return setErr("Enter the jobsite.");
    if (done < total) return setErr(`Mark every item pass / fail / n/a (${total - done} left).`);
    if (f.operator_signature.trim().length < 2) return setErr("Type your full name to sign.");
    setBusy(true); setErr(null);
    try {
      const sections = template.map((s) => ({ section_id: s.id, items: s.items.map((it) => ({ item_id: it.id, state: states[`${s.id}.${it.id}`] })) }));
      await api("/safety/checklists", { method: "POST", body: { ...f, customer_name: f.customer_name || null, free_notes: f.free_notes || null, sections, photos: strip(photos) } });
      toast.success("Checklist signed and submitted.");
      navigate("/safety");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <PortalPage eyebrow={`${done}/${total} ITEMS`} title="Pre-Job Checklist" back="/safety" backLabel="Safety Center" testId="checklist-page">
      {!template && !err && <Spinner />}
      {template && (
        <>
          <Card className="grid gap-3 sm:grid-cols-3">
            <input data-testid="checklist-jobsite" className={`${inputCls} sm:col-span-2`} placeholder="Jobsite address *" value={f.jobsite} onChange={(e) => setF({ ...f, jobsite: e.target.value })} />
            <input data-testid="checklist-date" type="date" className={inputCls} value={f.job_date} onChange={(e) => setF({ ...f, job_date: e.target.value })} />
            <input data-testid="checklist-customer" className={`${inputCls} sm:col-span-3`} placeholder="Customer (optional)" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
          </Card>
          {template.map((s) => (
            <Card key={s.id} data-testid={`checklist-section-${s.id}`}>
              <p className="mb-2 font-display text-xl font-bold uppercase">{s.title}</p>
              {s.items.map((it) => {
                const k = `${s.id}.${it.id}`;
                return (
                  <div key={k} className="flex flex-col gap-2 border-b border-[#2a2a2a] py-3 last:border-0 sm:flex-row sm:items-center">
                    <div className="flex-1"><p className="text-sm">{it.label}</p>{it.ref && <p className="flex items-center gap-1 text-[11px] text-neutral-500"><BookOpen size={11} />{it.ref}</p>}</div>
                    <div className="flex gap-1">
                      {[["pass", "PASS", "#4CCC06"], ["fail", "FAIL", "#FF3333"], ["na", "N/A", "#888"]].map(([v, label, c]) => (
                        <button key={v} onClick={() => setStates({ ...states, [k]: v })} data-testid={`check-${s.id}-${it.id}-${v}`} className="rounded-lg border px-3 py-1.5 text-[11px] font-extrabold" style={states[k] === v ? { background: c, borderColor: c, color: "#000" } : { borderColor: "#333", color: "#999" }}>{label}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </Card>
          ))}
          <Card className="space-y-3">
            <textarea data-testid="checklist-notes" rows={3} className={inputCls} placeholder="Notes (optional)" value={f.free_notes} onChange={(e) => setF({ ...f, free_notes: e.target.value })} />
            <Photos photos={photos} setPhotos={setPhotos} />
            <input data-testid="checklist-signature" className={inputCls} placeholder="Type your full name to sign" value={f.operator_signature} onChange={(e) => setF({ ...f, operator_signature: e.target.value })} />
          </Card>
        </>
      )}
      <ErrorNote>{err}</ErrorNote>
      {template && <Btn busy={busy} onClick={submit} data-testid="checklist-submit">Sign & submit checklist</Btn>}
    </PortalPage>
  );
}

export function IncidentReport() {
  const navigate = useNavigate();
  const [refs, setRefs] = useState(null);
  const [f, setF] = useState({ incident_type: "", severity: "no_injury", incident_date: new Date().toISOString().slice(0, 16), location: "", laser_model: "", power_pct: "", substrate: "", contaminant: "", narrative: "", immediate_actions: "", witnesses: "" });
  const [ppe, setPpe] = useState([]);
  const [flags, setFlags] = useState({ controlled_area: null, fire_watch_present: null, eyewear_od_verified: null });
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { api("/safety/ansi-refs").then((r) => setRefs(r.refs)).catch((e) => setErr(e.message)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ref = refs?.[f.incident_type];

  const submit = async () => {
    if (!f.incident_type) return setErr("Choose what happened.");
    if (!f.location.trim()) return setErr("Enter where it happened.");
    if (f.narrative.trim().length < 10) return setErr("Describe what happened (at least 10 characters).");
    if (f.immediate_actions.trim().length < 5) return setErr("Describe the immediate actions taken.");
    setBusy(true); setErr(null);
    try {
      await api("/safety/incidents", { method: "POST", body: {
        ...f, ...flags, incident_date: new Date(f.incident_date).toISOString(), power_pct: f.power_pct === "" ? null : Number(f.power_pct),
        laser_model: f.laser_model || null, substrate: f.substrate || null, contaminant: f.contaminant || null,
        ppe_worn: ppe, witnesses: f.witnesses.split(",").map((w) => w.trim()).filter(Boolean), photos: strip(photos),
      } });
      toast.success("Incident report filed — ALR corporate has been notified.");
      navigate("/safety");
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <PortalPage eyebrow="SAFETY INCIDENT" title="Report an Incident" back="/safety" backLabel="Safety Center" testId="incident-page">
      {!refs && !err && <Spinner />}
      {refs && (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="incident-types">
            {Object.entries(refs).map(([k, r]) => (
              <button key={k} onClick={() => setF({ ...f, incident_type: k })} data-testid={`incident-type-${k}`} className={`rounded-xl border p-3 text-left text-xs font-bold ${f.incident_type === k ? "border-[#FF3333] bg-[#FF3333]/10 text-white" : "border-[#333] bg-[#1A1A1A] text-neutral-300"}`}>{r.title}</button>
            ))}
          </div>
          {ref && (
            <Card className="space-y-1 text-xs text-neutral-400" data-testid="incident-refs">
              {[...(ref.ansi || []), ...(ref.ops_manual || [])].map((x) => <p key={x} className="flex gap-2"><BookOpen size={12} className="mt-0.5 shrink-0 text-[var(--accent)]" />{x}</p>)}
            </Card>
          )}
          <Card className="grid gap-3 sm:grid-cols-2">
            <select data-testid="incident-severity" className={inputCls} value={f.severity} onChange={set("severity")}>{SEVERITIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
            <input data-testid="incident-date" type="datetime-local" className={inputCls} value={f.incident_date} onChange={set("incident_date")} />
            <input data-testid="incident-location" className={`${inputCls} sm:col-span-2`} placeholder="Where did it happen? *" value={f.location} onChange={set("location")} />
            <input data-testid="incident-laser" className={inputCls} placeholder="Laser model (e.g. 1000W)" value={f.laser_model} onChange={set("laser_model")} />
            <input data-testid="incident-power" type="number" min={0} max={100} className={inputCls} placeholder="Power %" value={f.power_pct} onChange={set("power_pct")} />
            <input data-testid="incident-substrate" className={inputCls} placeholder="Substrate" value={f.substrate} onChange={set("substrate")} />
            <input data-testid="incident-contaminant" className={inputCls} placeholder="Contaminant" value={f.contaminant} onChange={set("contaminant")} />
          </Card>
          <Card className="space-y-3">
            <p className="text-[11px] font-bold tracking-[0.2em] text-neutral-400">PPE WORN</p>
            <div className="flex flex-wrap gap-2">{PPE.map((p) => <button key={p} onClick={() => setPpe(ppe.includes(p) ? ppe.filter((x) => x !== p) : [...ppe, p])} data-testid={`incident-ppe-${p.split(" ")[0].toLowerCase()}`} className={`rounded-full border px-3 py-1.5 text-xs ${ppe.includes(p) ? "border-[var(--accent)] text-[var(--accent)]" : "border-[#333] text-neutral-400"}`}>{p}</button>)}</div>
            {[["controlled_area", "Controlled area set up?"], ["fire_watch_present", "Fire watch present?"], ["eyewear_od_verified", "Eyewear OD verified?"]].map(([k, l]) => (
              <div key={k} className="flex items-center justify-between text-sm"><span>{l}</span><div className="flex gap-1">{[[true, "YES"], [false, "NO"]].map(([v, t]) => <button key={t} onClick={() => setFlags({ ...flags, [k]: v })} data-testid={`incident-${k}-${t.toLowerCase()}`} className={`rounded-lg border px-3 py-1 text-[11px] font-bold ${flags[k] === v ? "border-[var(--accent)] bg-[var(--accent)] text-black" : "border-[#333] text-neutral-400"}`}>{t}</button>)}</div></div>
            ))}
          </Card>
          <Card className="space-y-3">
            <textarea data-testid="incident-narrative" rows={4} className={inputCls} placeholder="What happened? *" value={f.narrative} onChange={set("narrative")} />
            <textarea data-testid="incident-actions" rows={3} className={inputCls} placeholder="Immediate actions taken *" value={f.immediate_actions} onChange={set("immediate_actions")} />
            <input data-testid="incident-witnesses" className={inputCls} placeholder="Witnesses (comma separated)" value={f.witnesses} onChange={set("witnesses")} />
            <Photos photos={photos} setPhotos={setPhotos} />
          </Card>
        </>
      )}
      <ErrorNote>{err}</ErrorNote>
      {refs && <Btn busy={busy} onClick={submit} data-testid="incident-submit">Submit incident report</Btn>}
    </PortalPage>
  );
}
